<template>
	<div class="popper" :class="[settings.class, 'grid-mode' ?? settings.isInGrid]" :style="settings.styles">
		<a ref="popperBtn" class="popper-btn" v-if="!settings.hideIcon">
			<icon :class="settings.iconClass" :icon="settings.icon" />
		</a>
		<div class="popper-tooltip" :class="settings.alertType ? `alert-${settings.alertType}` : ''" ref="popperShow" v-if="!settings.hideTooltip" style="display: none;">
			{{ content }}
			<slot></slot>
			<template v-if="settings.getContentFromOptions">{{ settings.content }}</template>
			<div class="tooltip-arrow" data-popper-arrow></div>
		</div>
	</div>
</template>

<script>
	import { createPopper } from '@popperjs/core'
	import merge from 'lodash/merge'
	import NikanAnimation from '@/plugins/anime'

	export default {
		name: 'popper',
		props: {
			options: { type: Object },
			content: { type: String }
		},
		data() {
			return {
				settings: {
					icon: 'info-circle',
					target: null,
					styles: {},
					class: null,
					hideIcon: false,
					position: 'top',
					hideTooltip: false,
					alertType: '',
					isInGrid: false, // applies some styles to popper to work as expected
					getContentFromOptions: false,
					content: '',
					events: {
						show: ['mouseenter', 'focus'],
						hide: ['mouseleave', 'blur']
					}
				}
			}
		},
		mounted() {
			this.settings = merge(this.settings, this.options)

			const target = this.getTarget()
			const tooltip = this.$refs.popperShow

			const options = {
				modifiers: [
					{
						name: 'offset',
						options: {
							offset: [0, 8],
						},
					},
				],
				placement: this.settings.position
			}

			const popperInstance = createPopper(target, tooltip, options)

			this.settings.events.show.forEach((event) => {
				target.addEventListener(event, () => this.show(popperInstance, tooltip))
			})

			this.settings.events.hide.forEach((event) => {
				target.addEventListener(event, () => this.hide(popperInstance, tooltip))
			})
		},

		methods: {
			getTarget() {
				let target = this.settings.target
				if (target) {
					if (typeof target === 'string') {
						return document.querySelector(target)
					}
					else {
						return target
					}
				}
				else {
					return this.$refs.popperBtn
				}
			},
			show(popperInstance, tooltip) {
				// Make the tooltip visible
				let animation = new NikanAnimation(tooltip, {
					opacity: [0, 1],
					duration: 1000,
					easing: 'easeOutExpo',
				})

				tooltip.style.display = 'block'
				animation.play()

				// Enable the event listeners
				popperInstance.setOptions((options) => ({
					...options,
					modifiers: [
						...options.modifiers,
						{ name: 'eventListeners', enabled: true },
					],
				}))

				// Update its position
				popperInstance.update()
			},
			hide(popperInstance, tooltip) {
				// Hide the tooltip
				let animation = new NikanAnimation(tooltip, {
					opacity: [1, 0],
					duration: 1000,
					easing: 'easeOutExpo',
				})

				animation.play()
				tooltip.style.display = 'none'


				// Disable the event listeners
				popperInstance.setOptions((options) => ({
					...options,
					modifiers: [
						...options.modifiers,
						{ name: 'eventListeners', enabled: false },
					],
				}))
			}
		}
	}
</script>

<style lang="less">
	@tooltip-border: 1px solid #979797;

	.popper {
		display: inline-block;
		position: relative;
		top: 3px;


		&.grid-mode {
			overflow: visible !important;

			&:hover {
				z-index: 10;
			}
		}
	}

	.popper-tooltip {
		background: white;
		color: #595959;
		font-weight: bold;
		padding: 4px 8px;
		font-size: 13px;
		border-radius: 4px;
		border: @tooltip-border;
		box-shadow: 0px 0px 4px 2px #00000042;
		white-space: nowrap;
	}

	.tooltip-arrow,
	.tooltip-arrow::before {
		position: absolute;
		width: 8px;
		height: 8px;
		background: inherit;
	}

	.tooltip-arrow {
		visibility: hidden;
	}

	.tooltip-arrow::before {
		visibility: visible;
		content: "";
		transform: rotate(45deg);
	}

	.popper-tooltip[data-popper-placement^="top"]>.tooltip-arrow {
		bottom: -4px;

		&::before {
			border-bottom: @tooltip-border;
			border-right: @tooltip-border;
		}
	}

	.popper-tooltip[data-popper-placement^="bottom"]>.tooltip-arrow {
		top: -4px;

		&::before {
			border-top: @tooltip-border;
			border-left: @tooltip-border;
		}
	}

	.popper-tooltip[data-popper-placement^="left"]>.tooltip-arrow {
		right: -4px;

		&::before {
			border-top: @tooltip-border;
			border-right: @tooltip-border;
		}
	}

	.popper-tooltip[data-popper-placement^="right"]>.tooltip-arrow {
		left: -4px;

		&::before {
			border-bottom: @tooltip-border;
			border-left: @tooltip-border;
		}
	}

	.popper-tooltip {
		display: block;
		opacity: 0;
	}

	.popper-btn {
		border: none;
		outline: none;
		background-color: transparent;
		cursor: pointer !important;
	}

	.popper-btn i {
		color: #1f8eff;
		font-size: 14px;
	}
</style>